import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiArrowRight, FiEdit3, FiUploadCloud } from "react-icons/fi";
import { CVUploadModal } from "src/features/pro-portfolio/cv-prefill/cv-upload-modal";
import { PrefillReview } from "src/features/pro-portfolio/cv-prefill/prefill-review";
import { PrefillStep, ResumeData } from "src/features/pro-portfolio/cv-prefill/types";
import { PrefillImages } from "src/features/pro-portfolio/cv-prefill/prefill-images";
import { SampleLaborPassModal } from "src/features/pro-portfolio/cv-prefill/sample-laborpass-modal";
import { cn } from "src/lib/utils";
import { logEvent } from "src/utlis/analytics";

const laborPassChecklist = [
	"A professional summary that highlights your expertise",
	"Detailed employment history with descriptions",
	"Photos of your completed work and projects",
	"Relevant certifications and qualifications",
	"Education background and training",
	"A complete profile that builds client trust",
];

const laborPassBenefits = [
	"Get matched with more job opportunities",
	"Stand out to clients and employers",
	"Build credibility with verified work history",
	"Increase your earning potential",
];

const SetupLaborPass: React.FC = () => {
	const navigate = useNavigate();
	const [step, setStep] = useState<PrefillStep>("interstitial");
	const [isUploadOpen, setIsUploadOpen] = useState(false);
	const [isSampleOpen, setIsSampleOpen] = useState(false);
	const [extractedData, setExtractedData] = useState<ResumeData | null>(null);

	const handleUploadSuccess = useCallback((data: ResumeData) => {
		setExtractedData(data);
		setIsUploadOpen(false);
		setStep("review");
	}, []);

	const handlePrefillComplete = useCallback(() => {
		setStep("images");
	}, []);

	const handleImagesComplete = useCallback(() => {
		navigate("/my-laborpass");
	}, [navigate]);

	const handleStartOver = useCallback(() => {
		setExtractedData(null);
		setStep("interstitial");
	}, []);

	const handleManualFill = useCallback(() => {
		logEvent("manual_fill_path_selected", { platform: "web" });
		navigate("/my-laborpass");
	}, [navigate]);

	const handleUploadClick = useCallback(() => {
		logEvent("cv_upload_path_selected", { platform: "web" });
		setIsUploadOpen(true);
	}, []);

	useEffect(() => {
		logEvent("laborpass_setup_viewed", { platform: "web" });
	}, []);

	// Image attachment step
	if (step === "images") {
		return <PrefillImages onComplete={handleImagesComplete} />;
	}

	// Review step
	if (step === "review" && extractedData) {
		return (
			<PrefillReview
				resumeData={extractedData}
				onComplete={handlePrefillComplete}
				onStartOver={handleStartOver}
			/>
		);
	}

	// Interstitial step (default)
	return (
		<>
			<div className="flex min-h-[70vh] flex-col justify-between border border-[#C9C9C9] bg-white">
				<div className="space-y-8 px-4 py-6 lg:space-y-10 lg:px-8 lg:py-12">
					<div className="max-w-3xl space-y-3">
						<h1 className="font-[AeonikPro] text-[1.75rem] font-bold leading-tight text-[#121212] lg:text-[2rem]">
							Set up your LaborPass
						</h1>
						<p className="text-base text-[#666666] lg:text-lg">
							How would you like to get started?
						</p>
					</div>

					<div className="grid gap-4 lg:grid-cols-2 lg:gap-8">
						<ActionCard
							title="Upload my CV"
							description="We'll read your CV and automatically fill in your work history, education, and skills. You review everything before saving."
							ctaLabel="Get started"
							icon={FiUploadCloud}
							variant="primary"
							onClick={handleUploadClick}
						/>
						<ActionCard
							title="Fill in manually"
							description="Prefer to enter your details step by step at your own pace? Continue to the manual form."
							ctaLabel="Continue manually"
							icon={FiEdit3}
							variant="secondary"
							onClick={handleManualFill}
						/>
					</div>

					<div className="grid gap-6 lg:grid-cols-2 lg:gap-8">
						<GuidanceCard
							title="What a great LaborPass looks like"
							accentClassName="bg-[#2F9E6B]"
							points={laborPassChecklist}
						/>
						<GuidanceCard
							title="Benefits of a complete LaborPass"
							accentClassName="bg-[#ED6E5C]"
							points={laborPassBenefits}
						/>
					</div>
				</div>

				<div
					data-testid="setup-laborpass-footer"
					className="border-t border-[#E7E7E7] px-4 py-4 lg:px-8 lg:py-6"
				>
					<div className="flex flex-col gap-4 border border-[#E7E7E7] bg-[#FCFCFC] px-4 py-5 lg:gap-5 lg:px-7">
						<div className="max-w-2xl space-y-1">
							<p className="text-base font-medium leading-snug text-[#303030] lg:text-lg">
								Ready to set up your LaborPass?
							</p>
							<p className="text-base leading-snug text-[#545454] lg:text-lg">
								Upload your CV to get started in seconds, or fill in your details
								manually.
							</p>
						</div>

						<div className="flex flex-col gap-3 lg:ml-auto lg:flex-row lg:flex-wrap lg:justify-end lg:items-center">
							<FooterButton variant="secondary" onClick={handleManualFill}>
								Fill in manually
							</FooterButton>
							{/* <FooterButton
								variant="secondary"
								onClick={() => setIsSampleOpen(true)}
							>
								View sample LaborPass
							</FooterButton> */}
							<FooterButton variant="primary" onClick={handleUploadClick}>
								Upload my CV
							</FooterButton>
						</div>
					</div>
				</div>
			</div>

			<CVUploadModal
				isOpen={isUploadOpen}
				onClose={() => setIsUploadOpen(false)}
				onSuccess={handleUploadSuccess}
				onManualFill={handleManualFill}
			/>
			<SampleLaborPassModal
				isOpen={isSampleOpen}
				onOpenChange={setIsSampleOpen}
			/>
		</>
	);
};

interface ActionCardProps {
	title: string;
	description: string;
	ctaLabel: string;
	icon: React.ComponentType<{ className?: string }>;
	variant: "primary" | "secondary";
	onClick: () => void;
}

const ActionCard = ({
	title,
	description,
	ctaLabel,
	icon: Icon,
	variant,
	onClick,
}: ActionCardProps) => {
	const isPrimary = variant === "primary";

	return (
		<div
			className={cn(
				"group flex h-full min-h-[220px] flex-col border p-6 text-left transition duration-200 lg:min-h-[236px] lg:p-7",
				isPrimary
					? "border-[#ED6E5C] bg-[#ED6E5C] text-white"
					: "border-[#D7D7D7] bg-white text-[#121212]"
			)}
		>
			<div
				className={cn(
					"mb-5 flex h-14 w-14 items-center justify-center border",
					isPrimary
						? "border-white/20 bg-white/15"
						: "border-[#F9CFC9] bg-[#FBE2DE]"
				)}
			>
				<Icon className={cn("h-6 w-6", isPrimary ? "text-white" : "text-[#ED6E5C]")} />
			</div>
			<div className="space-y-3">
				<h2 className="font-[AeonikPro] text-[1.5rem] font-semibold leading-tight lg:text-[1.75rem]">
					{title}
				</h2>
				<p
					className={cn(
						"max-w-[29rem] text-sm leading-6 lg:text-base lg:leading-7",
						isPrimary ? "text-white/90" : "text-[#767676]"
					)}
				>
					{description}
				</p>
			</div>
			<div className="mt-auto flex w-full justify-end pt-6">
				<button
					type="button"
					onClick={onClick}
					className={cn(
						"inline-flex w-full items-center justify-center gap-3 border px-4 py-3 text-base font-semibold transition focus:outline-none focus:ring-2 focus:ring-[#ED6E5C] focus:ring-offset-2 lg:w-auto lg:min-w-[220px]",
						isPrimary
							? "border-white/30 bg-white/10 text-white hover:bg-white/18"
							: "border-[#ED6E5C] text-[#ED6E5C] hover:bg-[#FFF3F0]"
					)}
				>
					<span>{ctaLabel}</span>
					<FiArrowRight className="h-5 w-5 transition group-hover:translate-x-1" />
				</button>
			</div>
		</div>
	);
};

interface GuidanceCardProps {
	title: string;
	accentClassName: string;
	points: string[];
}

const GuidanceCard = ({
	title,
	accentClassName,
	points,
}: GuidanceCardProps) => (
	<div className="border border-[#E7E7E7] bg-white p-6 lg:p-8">
		<h3 className="font-[AeonikPro] text-[1.4rem] font-semibold leading-tight text-[#121212] lg:text-[1.55rem]">
			{title}
		</h3>
		<div className="mt-5 space-y-4">
			{points.map((point) => (
				<div key={point} className="flex items-start gap-3">
					<span
						className={cn("mt-2 h-2.5 w-2.5 flex-shrink-0 rounded-full", accentClassName)}
					/>
					<p className="text-sm leading-6 text-[#545454] lg:text-[0.95rem]">
						{point}
					</p>
				</div>
			))}
		</div>
	</div>
);

interface FooterButtonProps {
	children: React.ReactNode;
	variant: "primary" | "secondary";
	onClick: () => void;
}

const FooterButton = ({
	children,
	variant,
	onClick,
}: FooterButtonProps) => (
	<button
		type="button"
		onClick={onClick}
	className={cn(
			"inline-flex min-h-12 w-full items-center justify-center gap-2 border px-5 py-2 text-base font-semibold transition focus:outline-none focus:ring-2 focus:ring-[#ED6E5C] focus:ring-offset-2 lg:w-auto",
			variant === "primary"
				? "border-[#ED6E5C] bg-[#ED6E5C] text-white hover:border-[#C55C4D] hover:bg-[#C55C4D]"
				: "border-[#D7D7D7] bg-white text-[#303030] hover:border-[#BEBEBE] hover:bg-[#FAFAFA]"
		)}
	>
		{children}
	</button>
);

export default SetupLaborPass;
